import React from 'react';
import WizardFlow from '../flow/WizardFlow';
import store from '../../../../redux/store';
import { toggleFilters } from '../../../../actions/filterActions';
import Breadcrumbs, { Crumb } from '../components/Breadcrumbs';
import ActionBar, { ActionButton, ActionButtonImg } from '../components/ActionBar';
import MuiIcons from './MuiIcons';

/**
 * @typedef {import('../flow/WizardFlowResponse').WizardFlowResponse} WizardFlowResponse
 * @typedef {import('../steps/BaseStep').default} BaseStep
 * @typedef {import('../data/WizardData').default} WizardData
 */

/**
 * @param {number} currentIndex - The current step index.
 * @param {WizardData} wizardData - The wizard data instance.
 * @see this.wizardData.stepIndex
 * @returns {JSX.Element} The rendered breadcrumbs UI.
 */
function buildBreadcrumbs(currentIndex, wizardData) {
  const crumbs = [];
  let stepDef = WizardFlow.getStepByIndex(0);
  let readyToNavigate = true;

  while (stepDef.isFound) {
    // Capture the current stepDef value for this iteration (prevents closure using the final one)
    const stepResponse = stepDef;
    const title = stepResponse.component.title;
    const stepChangeHandler = () => wizardData.navigationSetStep(stepResponse.index);
    const Component = stepResponse.component;
    const dataTest = `breadcrumb-${stepResponse.component.id}`;

    if (stepResponse.index === currentIndex) {
      crumbs.push(new Crumb(title, null, true, dataTest));
    } else if (readyToNavigate) {
      crumbs.push(new Crumb(title, stepChangeHandler, false, dataTest));
    } else {
      crumbs.push(new Crumb(title, null, false, dataTest));
    }

    if (!stepResponse.isLast) {
      crumbs.push(Crumb.getSeparator('>'));
    }

    // setting next stepDef
    readyToNavigate = readyToNavigate && Component.canProceed();
    stepDef = WizardFlow.getStepByIndex(stepResponse.index + 1);
  }

  return <Breadcrumbs crumbs={crumbs} />;
}

/**
 * Toggles the search filters panel.
 */
function toggleSearchFilters() {
  store.dispatch(toggleFilters());
}

/**
 * Builds the action menu for the current step.
 * @param {WizardFlowResponse} stepResult - The current step's flow response containing metadata.
 * @param {WizardData} wizardData - The wizard data instance for navigation actions.
 * @see this.wizardData.stepIndex
 * @returns {JSX.Element} The rendered ActionBar component with appropriate buttons.
 */
function buildActionMenu(stepResult, wizardData) {
  const buttons = [
    new ActionButtonImg('Filter', '/assets/images/icons/filter.svg', () => toggleSearchFilters(), {
      disabled: !stepResult.component.hasFilter(),
      dataTest: 'wizard-toggle-filter-button',
    }),
    new ActionButton('Previous', MuiIcons.icons.previous, () => wizardData.navigationPrevStep(), {
      disabled: stepResult.isFirst,
      dataTest: 'wizard-previous-button',
    }),
    new ActionButton('Next', MuiIcons.icons.next, () => wizardData.navigationNextStep(), {
      disabled: stepResult.isLast || !stepResult.component.canProceed(),
      dataTest: 'wizard-next-button',
    }),
  ];

  return <ActionBar buttons={buttons} />;
}

/**
 * Helper utility object for the Multi-Media Wizard UI components.
 */
export default {
  buildBreadcrumbs,
  buildActionMenu,
};
